import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  KeyIcon, PlusIcon, TrashIcon, NoSymbolIcon,
  ClipboardDocumentIcon, CheckIcon, ExclamationTriangleIcon,
  EnvelopeIcon, ChevronDownIcon, ChevronUpIcon,
  ClockIcon, SignalIcon,
} from '@heroicons/react/24/outline';
import axios from '../../../utils/axios';
import { REACT_APP_UPLOADS } from '../../../constants';
import { AuthContext } from '../../../contexts/Auth/Auth.Context';

/* ── Types ─────────────────────────────────────────────────────────── */

interface ApiKeyEntry {
  id: number; name: string; prefix: string; enabled: boolean;
  rateLimitRpm: number; lastUsedAt: string; createdAt: string;
}

interface UsageStats {
  total: number; last7d: number; last30d: number;
  errors: number; errorRate: number;
  daily: { day: string; count: number }[];
  topEndpoints: { endpoint: string; count: number }[];
  recent: { id: number; endpoint: string; method: string; statusCode: number; ipAddress: string; responseTimeMs: number; createdAt: string }[];
}

interface AccessRequest {
  id: number; userName: string; organisation: string; useCase: string;
  expectedVolume: string; contactEmail: string; status: string;
  adminNotes: string; createdAt: string;
}

/* ── Helpers ────────────────────────────────────────────────────────── */

function relTime(iso: string | undefined): string {
  if (!iso) return 'Never';
  const d = Date.now() - new Date(iso).getTime();
  const m = Math.floor(d / 60_000), h = Math.floor(d / 3_600_000), days = Math.floor(d / 86_400_000);
  if (m < 2) return 'Just now'; if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`; if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function statusColour(code: number): string {
  if (code < 300) return 'text-emerald-400'; if (code < 400) return 'text-blue-400';
  if (code < 500) return 'text-amber-400';   return 'text-red-400';
}

/* ── Mini bar sparkline ─────────────────────────────────────────────── */

function DailySparkline({ daily }: { daily: UsageStats['daily'] }) {
  if (!daily.length) return <p className="text-white/20 text-[10px] italic">No data yet</p>;
  const peak = Math.max(...daily.map((d) => d.count), 1);
  return (
    <div>
      <p className="text-white/30 text-[9px] uppercase tracking-wider mb-1.5">Last 14 days</p>
      <div className="flex items-end gap-0.5" style={{ height: 36 }}>
        {daily.map(({ day, count }) => (
          <div key={day} className="flex-1 flex flex-col items-center gap-0.5" title={`${day}: ${count}`}>
            <div className="w-full rounded-sm bg-green-500/60" style={{ height: `${Math.max((count / peak) * 30, 2)}px` }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-0.5">
        <span className="text-white/20 text-[8px]">{daily[0]?.day?.slice(5)}</span>
        <span className="text-white/20 text-[8px]">{daily[daily.length - 1]?.day?.slice(5)}</span>
      </div>
    </div>
  );
}

/* ── Usage panel (inline per key) ───────────────────────────────────── */

function UsagePanel({ keyId, headers }: { keyId: number; headers: Record<string, string> }) {
  const [stats,   setStats]   = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${REACT_APP_UPLOADS}/api/v1/api-keys/${keyId}/usage`, { headers })
      .then((r) => setStats(r.data))
      .catch((_e) => { /* silent */ })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyId]);

  if (loading) return <div className="text-white/25 text-xs py-4 text-center">Loading usage…</div>;
  if (!stats)  return <div className="text-white/20 text-xs py-4 text-center">No usage data</div>;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">

      {/* Summary metrics */}
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'All time',  value: stats.total.toLocaleString(),   color: 'text-white/80' },
            { label: 'Last 7d',   value: stats.last7d.toLocaleString(),  color: 'text-green-400' },
            { label: 'Errors',    value: `${stats.errorRate}%`,          color: stats.errorRate > 5 ? 'text-red-400' : 'text-white/40' },
          ].map((s) => (
            <div key={s.label} className="rounded-lg border border-white/5 px-3 py-2" style={{ background: 'rgba(0,0,0,0.2)' }}>
              <p className="text-white/25 text-[8px] uppercase tracking-wider">{s.label}</p>
              <p className={`text-sm font-bold tabular-nums mt-0.5 ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
        <DailySparkline daily={stats.daily} />
      </div>

      {/* Top endpoints */}
      <div>
        <p className="text-white/30 text-[9px] uppercase tracking-wider mb-1.5">Top Endpoints</p>
        {stats.topEndpoints.length === 0
          ? <p className="text-white/20 text-[10px] italic">No requests yet</p>
          : (
            <div className="flex flex-col gap-1">
              {stats.topEndpoints.map(({ endpoint, count }) => {
                const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                const label = endpoint.replace('/api/v1/', '/');
                return (
                  <div key={endpoint}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-white/50 text-[9px] font-mono truncate max-w-[160px]">{label}</span>
                      <span className="text-white/30 text-[9px] tabular-nums ml-2">{count}</span>
                    </div>
                    <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                      <div className="h-full bg-green-500/50 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )
        }
      </div>

      {/* Recent requests */}
      {stats.recent.length > 0 && (
        <div className="sm:col-span-2">
          <p className="text-white/30 text-[9px] uppercase tracking-wider mb-1.5">Recent Requests</p>
          <div className="rounded-lg border border-white/5 overflow-hidden" style={{ background: 'rgba(0,0,0,0.15)' }}>
            <table className="w-full text-[10px]">
              <thead>
                <tr className="border-b border-white/5">
                  {['Time', 'Method', 'Endpoint', 'Status', 'Ms', 'IP'].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-white/25 font-semibold uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stats.recent.slice(0, 10).map((r) => (
                  <tr key={r.id} className="border-t border-white/4 hover:bg-white/2">
                    <td className="px-3 py-1.5 text-white/30 whitespace-nowrap">{relTime(r.createdAt)}</td>
                    <td className="px-3 py-1.5 text-blue-400/70 font-mono">{r.method}</td>
                    <td className="px-3 py-1.5 text-white/50 font-mono truncate max-w-[160px]">{r.endpoint?.replace('/api/v1/', '/')}</td>
                    <td className={`px-3 py-1.5 font-mono font-semibold ${statusColour(r.statusCode)}`}>{r.statusCode}</td>
                    <td className="px-3 py-1.5 text-white/30 tabular-nums">{r.responseTimeMs}</td>
                    <td className="px-3 py-1.5 text-white/25 font-mono">{r.ipAddress}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Request access form (non-admin users) ──────────────────────────── */

function ApiAccessRequest({ userName, userEmail }: { userName: string; userEmail: string }) {
  const { state: authState } = useContext(AuthContext);
  const [org,     setOrg]     = useState('');
  const [email,   setEmail]   = useState(userEmail);
  const [useCase, setUseCase] = useState('');
  const [volume,  setVolume]  = useState('');
  const [sent,    setSent]    = useState(false);
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!org.trim() || !useCase.trim()) { setError('Please fill in all required fields.'); return; }
    setError(''); setSending(true);
    try {
      await axios.post(
        `${REACT_APP_UPLOADS}/api/v1/api-access-requests`,
        { userName, organisation: org.trim(), useCase: useCase.trim(), expectedVolume: volume.trim(), contactEmail: email.trim() },
        { headers: { Authorization: `Bearer ${authState.userToken ?? ''}` } },
      );
      setSent(true);
    } catch {
      setError('Failed to submit your request. Please try again or contact support.');
    } finally { setSending(false); }
  }

  if (sent) return (
    <div className="flex flex-col items-center py-20 gap-4 text-center">
      <div className="w-14 h-14 rounded-2xl bg-green-700/20 border border-green-500/25 flex items-center justify-center">
        <CheckIcon className="w-7 h-7 text-green-400" />
      </div>
      <p className="text-white/80 text-sm font-semibold">Request sent!</p>
      <p className="text-white/35 text-xs max-w-xs leading-relaxed">Our team will respond within 2 business days with your credentials once access is approved.</p>
      <button onClick={() => { setSent(false); setOrg(''); setEmail(''); setUseCase(''); setVolume(''); }} className="mt-2 text-[10px] text-white/25 hover:text-white/50 transition-colors">Submit another request</button>
    </div>
  );

  return (
    <div className="max-w-lg">
      <div className="mb-6 rounded-xl border border-amber-500/20 p-4" style={{ background: 'rgba(251,191,36,0.05)' }}>
        <div className="flex items-start gap-3">
          <KeyIcon className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-300 text-xs font-semibold mb-1">API access is managed by our team</p>
            <p className="text-white/40 text-xs leading-relaxed">Keys are provisioned after a short review. Fill in the form and we&apos;ll respond within 2 business days.</p>
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {[
          { id: 'req-org',     label: 'Organisation / Project *', val: org,     set: setOrg,     ph: 'e.g. Kenya Wildlife Service…', type: 'text' },
          { id: 'req-email',   label: 'Contact email *',          val: email,   set: setEmail,   ph: 'email to receive your key',    type: 'email' },
        ].map((f) => (
          <div key={f.id}>
            <label htmlFor={f.id} className="block text-white/40 text-[10px] uppercase tracking-wider mb-1.5">{f.label}</label>
            <input id={f.id} type={f.type} value={f.val} onChange={(e) => { f.set(e.target.value); setError(''); }}
              placeholder={f.ph} className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20" />
          </div>
        ))}
        <div>
          <label htmlFor="req-usecase" className="block text-white/40 text-[10px] uppercase tracking-wider mb-1.5">Intended use case *</label>
          <textarea id="req-usecase" value={useCase} onChange={(e) => { setUseCase(e.target.value); setError(''); }} rows={3}
            placeholder="Describe how you plan to use the API…"
            className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20 resize-none" />
        </div>
        <div>
          <label htmlFor="req-volume" className="block text-white/40 text-[10px] uppercase tracking-wider mb-1.5">Expected request volume</label>
          <input id="req-volume" value={volume} onChange={(e) => setVolume(e.target.value)} placeholder="e.g. ~500 req/day, weekly batch sync…"
            className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20" />
        </div>
        {error && <p className="text-red-400 text-[10px]">{error}</p>}
        <button type="submit" disabled={sending}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-green-700 hover:bg-green-600 text-white text-xs font-semibold transition-colors disabled:opacity-50 w-fit">
          <EnvelopeIcon className="w-3.5 h-3.5" />
          {sending ? 'Sending…' : 'Submit Request'}
        </button>
      </form>
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────────────── */

export default function ApiKeysView() {
  const { state }  = useContext(AuthContext);
  const token      = state.userToken ?? '';
  const role       = state.userData?.role?.toLowerCase() ?? 'user';
  const isAdmin    = role === 'admin' || role === 'super-admin';
  const userName   = state.userData?.name || state.userData?.username || 'User';
  const userEmail  = state.userData?.username || '';

  const [tab,          setTab]          = useState<'keys' | 'requests'>('keys');
  const [keys,         setKeys]         = useState<ApiKeyEntry[]>([]);
  const [requests,     setRequests]     = useState<AccessRequest[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [reqLoading,   setReqLoading]   = useState(false);
  const [expandedKey,  setExpandedKey]  = useState<number | null>(null);
  const [creating,     setCreating]     = useState(false);
  const [createdKey,   setCreatedKey]   = useState<string | null>(null);
  const [copied,       setCopied]       = useState(false);
  const [revoking,     setRevoking]     = useState<number | null>(null);
  const [deleting,     setDeleting]     = useState<number | null>(null);

  /* create form state */
  const [newName,   setNewName]   = useState('');
  const [newOrg,    setNewOrg]    = useState('');
  const [newEmail,  setNewEmail]  = useState('');
  const [nameError, setNameError] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);

  const headers = { Authorization: `Bearer ${token}` };

  /* ── Load keys (admin) ── */
  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    axios.get(`${REACT_APP_UPLOADS}/api/v1/api-keys`, { headers })
      .then((r) => setKeys(r.data ?? []))
      .catch((_e) => { /* silent */ })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAdmin]);

  /* ── Load access requests (admin) ── */
  useEffect(() => {
    if (!isAdmin || tab !== 'requests') return;
    setReqLoading(true);
    axios.get(`${REACT_APP_UPLOADS}/api/v1/api-access-requests`, { headers })
      .then((r) => setRequests(r.data ?? []))
      .catch((_e) => { /* silent */ })
      .finally(() => setReqLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isAdmin]);

  useEffect(() => {
    if (creating) { const t = setTimeout(() => nameInputRef.current?.focus(), 30); return () => clearTimeout(t); }
  }, [creating]);

  /* ── Create & Share ── */
  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) { setNameError('Key name is required.'); return; }
    setNameError('');
    try {
      const res = await axios.post(`${REACT_APP_UPLOADS}/api/v1/api-keys`,
        { name: newName.trim(), organisationName: newOrg.trim(), organisationEmail: newEmail.trim() },
        { headers });
      setCreatedKey(res.data.key);
      setKeys((prev) => [{ id: res.data.id, name: res.data.name, prefix: res.data.prefix, enabled: true, rateLimitRpm: 60, lastUsedAt: '', createdAt: res.data.createdAt }, ...prev]);
      setNewName(''); setNewOrg(''); setNewEmail(''); setCreating(false);
    } catch { setNameError('Failed to create API key.'); }
  }

  function handleCopy(text: string) {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  const handleRevoke = useCallback(async (id: number) => {
    setRevoking(id);
    try {
      await axios.put(`${REACT_APP_UPLOADS}/api/v1/api-keys/${id}/revoke`, {}, { headers });
      setKeys((prev) => prev.map((k) => k.id === id ? { ...k, enabled: false } : k));
    } catch (_e) { /* silent */ } finally { setRevoking(null); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleDelete = useCallback(async (id: number) => {
    setDeleting(id);
    try {
      await axios.delete(`${REACT_APP_UPLOADS}/api/v1/api-keys/${id}`, { headers });
      setKeys((prev) => prev.filter((k) => k.id !== id));
    } catch (_e) { /* silent */ } finally { setDeleting(null); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function handleReview(id: number, status: 'approved' | 'rejected') {
    try {
      const res = await axios.put(
        `${REACT_APP_UPLOADS}/api/v1/api-access-requests/${id}/review`,
        { status },
        { headers },
      );
      setRequests((prev) => prev.map((r) => r.id === id ? { ...r, status } : r));
      // If approval auto-created a key, surface it and reload the keys list
      if (res.data?.key) {
        setCreatedKey(res.data.key);
        setTab('keys');
        // Reload keys so the new one appears in the list
        axios.get(`${REACT_APP_UPLOADS}/api/v1/api-keys`, { headers })
          .then((r) => setKeys(r.data ?? []))
          .catch((_e) => { /* silent */ });
      }
    } catch (_e) { /* silent */ }
  }

  /* ── Non-admin: request form ── */
  if (!isAdmin) return (
    <div className="flex-1 overflow-y-auto p-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>
      <div className="mb-6">
        <h2 className="text-white/85 text-base font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Request API Access</h2>
        <p className="text-white/30 text-xs mt-0.5">Partner organisations can query WITS data programmatically via our secure API.</p>
      </div>
      <ApiAccessRequest userName={userName} userEmail={userEmail} />
    </div>
  );

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  /* ── Admin view ── */
  return (
    <div className="flex-1 overflow-y-auto p-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>

      {/* Header */}
      <div className="mb-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-white/85 text-base font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Developer API Keys</h2>
          <p className="text-white/30 text-xs mt-0.5">Manage partner keys, track usage, and action access requests.</p>
        </div>
        <button onClick={() => { setCreating(true); setNameError(''); }}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-700 hover:bg-green-600 text-white text-xs font-semibold transition-colors">
          <PlusIcon className="w-3.5 h-3.5" /> Create &amp; Share
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 mb-5 bg-white/4 rounded-xl p-1 w-fit">
        {([
          { id: 'keys',     label: `Keys (${keys.length})` },
          { id: 'requests', label: `Requests${pendingCount > 0 ? ` (${pendingCount} pending)` : ''}` },
        ] as const).map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${tab === t.id ? 'bg-green-700/70 text-green-300 border border-green-500/25' : 'text-white/35 hover:text-white/60'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* One-time key reveal banner */}
      {createdKey && (
        <div className="mb-5 rounded-xl border border-emerald-500/30 p-4" style={{ background: 'rgba(16,185,129,0.06)' }}>
          <div className="flex items-center gap-2 mb-2">
            <ExclamationTriangleIcon className="w-4 h-4 text-amber-400 shrink-0" />
            <p className="text-amber-300 text-xs font-semibold">Copy this key now — it will never be shown again</p>
          </div>
          <div className="flex items-center gap-2 bg-black/20 rounded-lg px-3 py-2.5">
            <code className="flex-1 text-emerald-400 text-xs font-mono break-all">{createdKey}</code>
            <button onClick={() => handleCopy(createdKey)} className="shrink-0 p-1.5 rounded-md hover:bg-white/10 transition-colors" title="Copy">
              {copied ? <CheckIcon className="w-4 h-4 text-emerald-400" /> : <ClipboardDocumentIcon className="w-4 h-4 text-white/40" />}
            </button>
          </div>
          <button onClick={() => setCreatedKey(null)} className="mt-2 text-[10px] text-white/25 hover:text-white/50 transition-colors">I&apos;ve saved it — dismiss</button>
        </div>
      )}

      {/* Create & Share form */}
      {creating && (
        <div className="mb-5 rounded-xl border border-white/8 p-5" style={{ background: '#0a2e18' }}>
          <p className="text-white/60 text-xs font-semibold mb-4">New API Key</p>
          <form onSubmit={handleCreate} className="flex flex-col gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label htmlFor="new-key-name" className="block text-white/35 text-[9px] uppercase tracking-wider mb-1">Key name *</label>
                <input id="new-key-name" ref={nameInputRef} value={newName} onChange={(e) => { setNewName(e.target.value); setNameError(''); }}
                  placeholder="e.g. KWS Portal"
                  className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20" />
              </div>
              <div>
                <label htmlFor="new-org-name" className="block text-white/35 text-[9px] uppercase tracking-wider mb-1">Organisation</label>
                <input id="new-org-name" value={newOrg} onChange={(e) => setNewOrg(e.target.value)}
                  placeholder="e.g. Kenya Wildlife Service"
                  className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20" />
              </div>
              <div>
                <label htmlFor="new-org-email" className="block text-white/35 text-[9px] uppercase tracking-wider mb-1">Org email (auto-send key)</label>
                <input id="new-org-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="contact@organisation.org"
                  className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20" />
              </div>
            </div>
            {nameError && <p className="text-red-400 text-[10px]">{nameError}</p>}
            {newEmail && <p className="text-green-400/60 text-[10px]">✓ Key will be emailed to {newEmail} automatically</p>}
            <div className="flex gap-2">
              <button type="submit" className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-700 hover:bg-green-600 text-white text-xs font-semibold transition-colors">
                <EnvelopeIcon className="w-3.5 h-3.5" /> Create &amp; Share
              </button>
              <button type="button" onClick={() => { setCreating(false); setNewName(''); setNewOrg(''); setNewEmail(''); setNameError(''); }}
                className="px-3 py-2 rounded-lg border border-white/10 text-white/40 text-xs hover:bg-white/5 transition-colors">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* ── Keys tab ── */}
      {tab === 'keys' && (
        loading ? <div className="text-white/25 text-xs text-center py-12">Loading…</div>
        : keys.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-12 h-12 rounded-2xl border border-white/8 flex items-center justify-center" style={{ background: '#0a2e18' }}>
              <KeyIcon className="w-6 h-6 text-white/15" />
            </div>
            <p className="text-white/30 text-sm">No API keys yet</p>
            <p className="text-white/15 text-xs max-w-xs text-center">Create keys for approved partner organisations.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {keys.map((key) => (
              <div key={key.id} className={`rounded-xl border overflow-hidden ${key.enabled ? 'border-white/6' : 'border-white/4 opacity-60'}`} style={{ background: '#0a2e18' }}>

                {/* Key row */}
                <div className="flex items-start gap-3 px-4 py-4">
                  <div className="w-8 h-8 rounded-lg border border-white/8 flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <KeyIcon className="w-4 h-4 text-green-400/60" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white/80 text-sm font-semibold">{key.name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase border ${key.enabled ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
                        {key.enabled ? 'Active' : 'Revoked'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 flex-wrap">
                      <span className="text-white/30 text-[10px] font-mono">{key.prefix}</span>
                      <span className="flex items-center gap-1 text-white/20 text-[10px]"><SignalIcon className="w-3 h-3" />{key.rateLimitRpm} req/min</span>
                      <span className="flex items-center gap-1 text-white/20 text-[10px]"><ClockIcon className="w-3 h-3" />Created {relTime(key.createdAt)}</span>
                      {key.lastUsedAt
                        ? <span className="text-white/20 text-[10px]">Last used {relTime(key.lastUsedAt)}</span>
                        : <span className="text-white/15 text-[10px]">Never used</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => setExpandedKey(expandedKey === key.id ? null : key.id)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/8 text-white/35 text-[10px] hover:text-green-400 hover:border-green-500/30 transition-colors">
                      {expandedKey === key.id ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
                      Usage
                    </button>
                    {key.enabled && (
                      <button onClick={() => handleRevoke(key.id)} disabled={revoking === key.id} title="Revoke"
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/8 text-white/30 text-[10px] hover:text-amber-400 hover:border-amber-500/30 transition-colors disabled:opacity-40">
                        <NoSymbolIcon className="w-3 h-3" />{revoking === key.id ? '…' : 'Revoke'}
                      </button>
                    )}
                    <button onClick={() => handleDelete(key.id)} disabled={deleting === key.id} title="Delete"
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/8 text-white/30 text-[10px] hover:text-red-400 hover:border-red-500/30 transition-colors disabled:opacity-40">
                      <TrashIcon className="w-3 h-3" />{deleting === key.id ? '…' : 'Delete'}
                    </button>
                  </div>
                </div>

                {/* Inline usage panel */}
                {expandedKey === key.id && (
                  <div className="border-t border-white/5 px-4 pb-4 pt-3" style={{ background: 'rgba(0,0,0,0.12)' }}>
                    <UsagePanel keyId={key.id} headers={headers} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {/* ── Requests tab ── */}
      {tab === 'requests' && (
        reqLoading ? <div className="text-white/25 text-xs text-center py-12">Loading…</div>
        : requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <EnvelopeIcon className="w-10 h-10 text-white/10" />
            <p className="text-white/30 text-sm">No access requests yet</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {requests.map((req) => (
              <div key={req.id} className="rounded-xl border border-white/6 px-5 py-4" style={{ background: '#0a2e18' }}>
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-white/80 text-sm font-semibold">{req.organisation}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border ${
                        req.status === 'pending'  ? 'bg-amber-500/15 text-amber-400 border-amber-500/20' :
                        req.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' :
                                                    'bg-red-500/15 text-red-400 border-red-500/20'
                      }`}>{req.status}</span>
                    </div>
                    <p className="text-white/40 text-xs leading-relaxed mb-2">{req.useCase}</p>
                    <div className="flex flex-wrap gap-4 text-[10px] text-white/30">
                      {req.userName     && <span>By: {req.userName}</span>}
                      {req.contactEmail && <span>Email: {req.contactEmail}</span>}
                      {req.expectedVolume && <span>Volume: {req.expectedVolume}</span>}
                      <span>{relTime(req.createdAt)}</span>
                    </div>
                  </div>

                  {req.status === 'pending' && (
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => handleReview(req.id, 'approved')}
                        className="px-3 py-1.5 rounded-lg bg-green-700 hover:bg-green-600 text-white text-xs font-semibold transition-colors">
                        Approve
                      </button>
                      <button onClick={() => handleReview(req.id, 'rejected')}
                        className="px-3 py-1.5 rounded-lg border border-red-500/30 text-red-400 text-xs hover:bg-red-500/10 transition-colors">
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
